import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { cloneDeep } from 'lodash';
import { getRichTextFieldContent } from './TicketHandler';
import { AutoPassSystemFieldInSchema } from '@/common/constant/SystemField';
import { checkModelUnsupport } from '@/component/system/handler/OrganizationProfileHandler';
import { 
    isShowFieldAIPDFPageSplitStatus, 
    isShowFieldCallApiTarget, 
    isShowFieldEventTypeChatThread, 
    isShowReasoningEffortField,
    // AI Telephone helper functions
    isShowRealtimeModelField,
    isShowChatGptModelFieldForTelephone,
    isShowReasoningEffortFieldForTelephone,
    isShowExecutionModeField,
    isShowExecutionScopeField,
    isShowApplicablePhoneNumberField,
    isShowVoiceSettingsField,
    isShowTimeExecutionFields,
    isShowTimeBasedDelayResponseField,
    isShowTriggerConditionField,
    isShowTimeExecutionPeriod
} from './KnowledgeTicketHandler';
    
/**
 * Filter required tag fields based on visibility conditions
 * @param {Array} listFieldRequired - List of required fields
 * @param {Object} aiAutoSelectedData - AI Auto form selected data
 * @param {Object} sdautoSelectedData - SD Auto form selected data  
 * @param {Object} aiTelephoneSelectedData - AI Telephone form selected data
 * @returns {Array} Filtered list of visible required fields
 */
export const filterTagFieldRequired = (listFieldRequired, aiAutoSelectedData, sdautoSelectedData) => {
    return listFieldRequired.filter(field => {
        // AI Auto logic
        if (field.sysField === Constant.AI_AUTO.SYSTEM_FIELD.CHAT_THREAD_TYPE_TRIGGER) {
            return !!(isShowFieldEventTypeChatThread(aiAutoSelectedData));
        }
        if (field.sysField === Constant.AI_AUTO.SYSTEM_FIELD.AI_PDF_PAGE_SPLIT_STATUS) {
            return !!(isShowFieldAIPDFPageSplitStatus(aiAutoSelectedData));
        }
        if (field?.sysField === Constant.AI_AUTO.SYSTEM_FIELD.REASONING_EFFORT) {
            return !!(isShowReasoningEffortField(aiAutoSelectedData));
        }
        
        // SD Auto logic
        if (field?.sysField === Constant.SD_AUTO.SYSTEM_FIELD.SDAUTO_CALL_SCRIPT_API_SETTING_TARGET) {
            return !!(isShowFieldCallApiTarget(sdautoSelectedData));
        }

        return true;
    });
}

/**
 * Check if all required fields have been filled
 * @param {Array} fieldListData - List of all fields
 * @param {Function} getValuesCreateQaTicket - Function to get form values
 * @param {Array} fileUploadList - List of uploaded files
 * @param {Array} fileUpdatedList - List of updated files
 * @param {Object} aiAutoSelectedData - AI Auto selected data
 * @param {Array} listChatGptModel - List of available GPT models
 * @param {Object} sdautoSelectedData - SD Auto selected data
 * @param {Object} aiTelephoneSelectedData - AI Telephone selected data
 * @returns {boolean}
 */
export const isInputAllFieldRequired = (fieldListData, getValuesCreateQaTicket, fileUploadList, fileUpdatedList = [], aiAutoSelectedData, listChatGptModel = [], sdautoSelectedData = {}, aiTelephoneSelectedData = {}) => {
    let listFieldRequired = fieldListData?.filter(field => field.structure.required && field.type !== Constant.FORM_TYPE.FIELD_WORKFLOW && !AutoPassSystemFieldInSchema.includes(field.sysField));
    let isTelephoneForm = fieldListData?.some(field => field.sysField?.startsWith(Constant.ID_PREFIX.AI_TELEPHONE_FIELD_ID_PREFIX));
    if (isTelephoneForm) {
        listFieldRequired = filterFieldRequiredForTelephone(listFieldRequired, aiTelephoneSelectedData);
    } else {
        listFieldRequired = filterTagFieldRequired(listFieldRequired, aiAutoSelectedData, sdautoSelectedData);
    }
    if (!Utils.isEmptyArray(listFieldRequired)) {
        let isInputFieldRequired = true;

        for (let field of listFieldRequired) {
            let fieldValue = getValuesCreateQaTicket(field.id);
            if (field.type === Constant.FORM_TYPE.FIELD_RICHTEXT) {
                if(!fieldValue) {
                    return false;
                }
                let textInput = getRichTextFieldContent(fieldValue);
                
                if (!textInput || textInput === '""') {
                    return false;
                }
            } else {
                if (field.sysField === 'attachments') {
                    if (!Utils.isEmptyArray(fileUploadList)) {
                        if (!Utils.isEmptyArray(fileUpdatedList)) {
                            let fileActualUpload = [];
                            fileUploadList.forEach(file => {
                                if (file.fileUrl) {
                                    let isFileIsDelete = fileUpdatedList.find(
                                        fileUpdata =>
                                            fileUpdata?.fileUrl === file.fileUrl &&
                                            fileUpdata?.isDelete
                                    );
                                    if (!isFileIsDelete) {
                                        fileActualUpload.push(file);
                                    }
                                }
                            });

                            if (!Utils.isEmptyArray(fileActualUpload)) {
                                isInputFieldRequired = true;
                            } else {
                                return false;
                            }
                        } else {
                            isInputFieldRequired = true;
                        }
                    } else {
                        return false;
                    }
                    break;
                } else if (
                    Utils.isEmpty(fieldValue) ||
                    fieldValue === '' ||
                    (field.type === Constant.FORM_TYPE.FIELD_TAG &&
                        (Utils.isEmptyArray(fieldValue) || (Array.isArray(fieldValue) && typeof fieldValue[0] === 'object' && fieldValue.some(i => i?.value === null))))
                ) {
                    return false;
                } else if (field?.type === Constant.FORM_TYPE.FIELD_TAG && field?.sysField === Constant.AI_AUTO.SYSTEM_FIELD.CHATGPT_MODEL) {
                    let isValid = checkModelUnsupport(fieldValue[0]?.title, listChatGptModel)
                    if (isValid) {
                        return false;
                    }
                }
            }
        }

        return isInputFieldRequired;
    }

    return true;
};

/**
 * isInputValueInCustomForm
 * validate is input value in customer form
 * @param {*} field
 * @param {*} fieldValue
 * @returns
 */
export const isInputValueInCustomForm = (field, fieldValue) => {
    if (!fieldValue) {
        return false;
    }

    if (field.type === Constant.FORM_TYPE.FIELD_RICHTEXT) {
        let textInput = getRichTextFieldContent(fieldValue);
        if (textInput) {
            return true;
        }
    }

    fieldValue = typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue;

    if (
        (fieldValue &&
            [
                Constant.FORM_TYPE.FIELD_TEXT,
                Constant.FORM_TYPE.FIELD_INT,
                Constant.FORM_TYPE.FIELD_URL,
                Constant.FORM_TYPE.FIELD_IFRAME,
                Constant.FORM_TYPE.FIELD_DATE,
                Constant.FORM_TYPE.FIELD_TIME,
                Constant.FORM_TYPE.FIELD_DATE_TIME
            ].includes(field.type)) ||
        (!Utils.isEmptyArray(fieldValue) && [Constant.FORM_TYPE.FIELD_TAG].includes(field.type))
    ) {
        return true;
    }

    return false;
};

export const getPanelType = (panelList, panelId) => {
    let currentPanelIndex = Utils.findIndexInArrayById(panelId, panelList);
    if (currentPanelIndex === -1) return '';

    let currentPanel = panelList[currentPanelIndex];
    return currentPanel.type;
};

export const updateUsingTicketOfFormListState = (currentCorporationFormList, setCurrentCorporationFormList, formSelectedId, newTicket) => {
    let formListClone = cloneDeep(currentCorporationFormList);
    let formIndex = Utils.findIndexInArrayById(formSelectedId, formListClone);
    if(formIndex !== -1) {
        let formDetail = formListClone[formIndex];
        let usingTickets = Utils.convertJSONDataToObject(formDetail.usingTickets);

        if (!Utils.isEmptyObject(usingTickets)) {
            if (usingTickets[newTicket.affiliationProject]) {
                usingTickets[newTicket.affiliationProject].push(newTicket.id);
            } else {
                usingTickets[newTicket.affiliationProject] = [newTicket.id];
            }
        } else {
            usingTickets = {};
            usingTickets[newTicket.affiliationProject] = [newTicket.id];
        }

        formListClone[formIndex].usingTickets = JSON.stringify(usingTickets);
    }
    setCurrentCorporationFormList(formListClone);
};

export const filterFieldRequiredForTelephone = (
    listFieldRequired,
    aiTelephoneSelectedData = {}
) => {
    const formDetail = aiTelephoneSelectedData?.selectedFormDetail;

    // Training format: no conditional fields, return all required fields
    if (Utils.isSystemFormAITrainingPrompt(formDetail)) {
        return listFieldRequired;
    }

    return listFieldRequired.filter(field => {
        // AI Telephone fields validation
        if (field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_REALTIME_MODEL) {
            return isShowRealtimeModelField(aiTelephoneSelectedData);
        }
        if (field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_MODE) {
            return isShowExecutionModeField(aiTelephoneSelectedData);
        }
        if (field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_EXECUTION_SCOPE) {
            return isShowExecutionScopeField(aiTelephoneSelectedData);
        }
        if (field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_APPLICABLE_PHONE_NUMBER) {
            return isShowApplicablePhoneNumberField(aiTelephoneSelectedData);
        }
        if (field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_VOICE_SETTINGS) {
            return isShowVoiceSettingsField(aiTelephoneSelectedData);
        }

        if(field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_TIME_BASED_DELAY_RESPONSE) {
            return isShowTimeBasedDelayResponseField(aiTelephoneSelectedData);
        }

        if(field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_TRIGGER_CONDITION){
            return isShowTriggerConditionField(aiTelephoneSelectedData)
        }

        if(field.sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_TIME_PERIOD){
            return isShowTimeExecutionPeriod(aiTelephoneSelectedData)
        }
        // Time execution fields
        if (
            [
                Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_START_TIME,
                Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_END_TIME,
                Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_START_DATE,
                Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_END_DATE,
                Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_DAY_OF_WEEKS,
                Constant.SYSTEM_FIELD.EXECUTION_TIMEZONE // TZL-001 (D7): drop required when 専用時間帯設定 is OFF (24/7)
            ].includes(field.sysField)
        ) {
            return isShowTimeExecutionFields(aiTelephoneSelectedData) && isShowTimeExecutionPeriod(aiTelephoneSelectedData);
        }
        // CHATGPT_MODEL and REASONING_EFFORT for AI Telephone form
        if (!Utils.isEmptyObject(aiTelephoneSelectedData)) {
            if (field.sysField === Constant.SYSTEM_FIELD.CHATGPT_MODEL) {
                return isShowChatGptModelFieldForTelephone(aiTelephoneSelectedData);
            }
            if (field.sysField === Constant.SYSTEM_FIELD.REASONING_EFFORT) {
                return isShowReasoningEffortFieldForTelephone(aiTelephoneSelectedData);
            }
        }

        return true;
    });
};